import { Receipt, Download } from 'lucide-react';
import type { PurchaseOrderInvoice } from '@/types/purchaseOrder.types';
import { etiquetaDeFactura } from '@/features/proveedores/lib/estadoDeFactura';
import { Badge } from '@/shared/components/Badge';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

interface TarjetaDeFacturaProps {
  invoice: PurchaseOrderInvoice;
  numero: number;
  /** Lleva a los pagos de la orden, que es donde se sube el complemento. */
  onSubirComplemento?: () => void;
}

/**
 * Una factura dentro del modal de facturas de la orden.
 *
 * Sale de PurchaseOrdersList —que pasa de dos mil lineas— al tocarla para
 * agregar el badge del complemento (C-09).
 */
export const TarjetaDeFactura = ({
  invoice,
  numero,
  onSubirComplemento,
}: TarjetaDeFacturaProps) => {
  const etiqueta = etiquetaDeFactura(invoice);

  return (
    <div className="bg-gray-50 rounded-lg p-5 border border-gray-200 hover:border-blue-300 transition-colors">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
            <Receipt className="w-5 h-5 text-green-600" />
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-semibold text-gray-900">
              Factura {numero}
            </h4>
            <p className="text-xs text-gray-500 mt-0.5">
              Subida el{' '}
              {new Date(invoice.created_at).toLocaleDateString('es-MX', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
        </div>

        {etiqueta && (
          <Badge color={etiqueta.color} className="flex-shrink-0">
            {etiqueta.texto}
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <a
          href={invoice.xml_url}
          download
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          Descargar XML
        </a>
        {invoice.pdf_url ? (
          <a
            href={invoice.pdf_url}
            download
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Descargar PDF
          </a>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-200 rounded-md">
            Sin PDF
          </span>
        )}

        {/* Solo cuando falta el complemento: el boton es la salida del bloqueo,
            y ponerlo siempre lo volveria un adorno. */}
        {etiqueta && onSubirComplemento && (
          <button
            type="button"
            onClick={onSubirComplemento}
            className={estiloDeBoton('primario')}
          >
            Subir complemento
          </button>
        )}
      </div>
    </div>
  );
};
